import { useTranslation } from 'react-i18next';
import type { LiveClockState } from '@/domain/live-clock';
import { cn } from '@/lib/cn';
import { LiveClockText } from './LiveClockText';
import { Icon } from '../common/icon';

export interface LiveEventChipProps {
  home: string;
  away: string;
  homeScore?: string;
  awayScore?: string;
  clock?: string;
  liveClockState?: LiveClockState;
  translatePeriod?: (key: string) => string;
  /** Show monitor-play when the event has a live stream and account is configured. */
  hasLiveStream?: boolean;
  selected?: boolean;
  onSelect?: () => void;
  className?: string;
}

export function LiveEventChip({
  home,
  away,
  homeScore = '0',
  awayScore = '0',
  clock,
  liveClockState,
  translatePeriod,
  hasLiveStream = false,
  selected = false,
  onSelect,
  className,
}: LiveEventChipProps) {
  const { t } = useTranslation();
  const liveStreamLabel = t('LiveStream', { defaultValue: 'Live stream available' });

  const content = (
    <>
      <div className="flex h-3.5 items-center gap-1.5">
        <Icon
          name="live-icon"
          size={12}
          className={cn(
            'block shrink-0 self-center',
            selected ? 'text-status-info' : 'text-text-secondary',
          )}
        />
        {clock || liveClockState ? (
          <LiveClockText
            liveClockState={liveClockState}
            clock={clock}
            translatePeriod={translatePeriod}
            className={cn(
              'flex min-w-0 flex-1 items-center text-[11px] leading-none whitespace-nowrap',
              selected ? 'text-status-info' : 'text-text-secondary',
            )}
          />
        ) : (
          <span className="min-w-0 flex-1" />
        )}
        {hasLiveStream ? (
          <Icon
            name="monitor-play"
            size={12}
            label={liveStreamLabel}
            className={cn(
              'block shrink-0 self-center',
              selected ? 'text-status-info' : 'text-text-secondary',
            )}
          />
        ) : null}
      </div>
      <TeamLine name={home} score={homeScore} selected={selected} />
      <TeamLine name={away} score={awayScore} selected={selected} />
    </>
  );

  const classes = cn(
    'flex h-[72px] w-[168px] shrink-0 flex-col gap-1 rounded-[10px] bg-bg-surface px-2.5 py-2 text-left',
    className,
  );

  if (onSelect) {
    return (
      <button type="button" onClick={onSelect} className={classes}>
        {content}
      </button>
    );
  }

  return <div className={classes}>{content}</div>;
}

function TeamLine({
  name,
  score,
  selected,
}: {
  name: string;
  score: string;
  selected: boolean;
}) {
  return (
    <div
      className={cn(
        'flex h-4 items-center gap-2 text-[12px] leading-4 font-bold',
        selected ? 'text-text-primary' : 'text-text-secondary',
      )}
    >
      <span className="w-3.5 shrink-0 text-center tabular-nums">{score}</span>
      <span className="min-w-0 flex-1 truncate">{name}</span>
    </div>
  );
}
